import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeftRight,
  Bell,
  CircleDot,
  Clock,
  Eye,
  Flag,
  ShieldCheck,
  Ticket,
  UserCheck,
  Users,
} from 'lucide-react';

/**
 * Support-app Settings sidebar config — drives the shell's `support-settings`
 * hover/pinned panel (projects `settings-tabs.ts` parity). MUST stay in sync
 * with apps/support/src/pages/SettingsHub/tabs.tsx (SETTINGS_TABS): the panel
 * links to /support/settings?tab=<id>, the page renders the matching panel.
 */
export interface SupportSettingsTabConfig {
  id: string;
  label: string;
  section: string;
  icon: LucideIcon;
  /** The support.* view key gating this tab (checked against the permission slice). */
  requiredPermission: string;
}

export const SUPPORT_SETTING_SECTIONS = ['General', 'Team management', 'Masters'] as const;

export const SUPPORT_SETTING_TABS: SupportSettingsTabConfig[] = [
  // General
  { id: 'roles', label: 'Roles & Permissions', section: 'General', icon: ShieldCheck, requiredPermission: 'support.rbac.view' },
  // Workspace notification policy (which notification kinds are delivered, per
  // channel: in-app + email).
  { id: 'notifications', label: 'Notifications & Email', section: 'General', icon: Bell, requiredPermission: 'support.settings.view' },
  // §4.10 — the ONE workspace calendar the SLA clock measures against, plus the
  // desk-wide coverage default. Restored 2026-07-23 (the panel was retired in W10).
  { id: 'business-hours', label: 'Business hours', section: 'General', icon: Clock, requiredPermission: 'support.settings.view' },
  // Team management
  { id: 'types', label: 'Ticket Types & Queries', section: 'Team management', icon: Ticket, requiredPermission: 'support.types.view' },
  // 2026-07: the Resolution + Escalation & Approvals tabs are retired — handling
  // config lives inside Teams & People (team drawer Handling tab + Desk defaults
  // sheet) and the type drawer's response-time card.
  { id: 'teams', label: 'Teams & People', section: 'Team management', icon: Users, requiredPermission: 'support.teams.view' },
  { id: 'watcher-rules', label: 'Watcher Rules', section: 'Team management', icon: Eye, requiredPermission: 'support.resolution.view' },
  // Masters — one entry per shipped master list (Notice Type + Watcher Type
  // removed from the nav 2026-07; the lists still exist backend-side).
  { id: 'availability-status', label: 'Availability Status', section: 'Masters', icon: UserCheck, requiredPermission: 'support.masters.view' },
  { id: 'priority', label: 'Priority', section: 'Masters', icon: Flag, requiredPermission: 'support.masters.view' },
  { id: 'ticket-status', label: 'Ticket Status', section: 'Masters', icon: CircleDot, requiredPermission: 'support.masters.view' },
  { id: 'transfer-reason', label: 'Transfer Reason', section: 'Masters', icon: ArrowLeftRight, requiredPermission: 'support.masters.view' },
];
